import { useState, useRef, useEffect, useCallback } from 'react';

const BeforeAfterComparison = ({
  beforeSrc,
  afterSrc,
  beforeLabel = 'Client Footage',
  afterLabel = 'MotionX Output',
}) => {
  const [sliderPosition, setSliderPosition] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const [isPlaying, setIsPlaying] = useState(true);

  const containerRef = useRef(null);
  const videoBeforeRef = useRef(null);
  const videoAfterRef = useRef(null);

  // Reset state when sources change
  useEffect(() => {
    setSliderPosition(50);
    setIsPlaying(true);
  }, [beforeSrc, afterSrc]);

  const handleMove = useCallback((clientX) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
    setSliderPosition(percentage);
  }, []);

  const handleMouseDown = useCallback(() => {
    setIsDragging(true);
  }, []);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleMouseMove = useCallback(
    (e) => {
      if (!isDragging) return;
      handleMove(e.clientX);
    },
    [isDragging, handleMove]
  );

  const handleTouchMove = useCallback(
    (e) => {
      if (e.touches.length > 0) {
        handleMove(e.touches[0].clientX);
      }
    },
    [handleMove]
  );

  const handleClick = useCallback(
    (e) => {
      handleMove(e.clientX);
    },
    [handleMove]
  );

  const togglePlayPause = useCallback(
    (e) => {
      e.stopPropagation();
      if (videoBeforeRef.current && videoAfterRef.current) {
        if (isPlaying) {
          videoBeforeRef.current.pause();
          videoAfterRef.current.pause();
        } else {
          videoBeforeRef.current.play();
          videoAfterRef.current.play();
        }
        setIsPlaying(!isPlaying);
      }
    },
    [isPlaying]
  );

  // Sync after video to before video
  useEffect(() => {
    const videoBefore = videoBeforeRef.current;
    const videoAfter = videoAfterRef.current;
    if (!videoBefore || !videoAfter) return;

    const handleSeeked = () => {
      videoAfter.currentTime = videoBefore.currentTime;
    };

    videoBefore.addEventListener('seeked', handleSeeked);
    return () => {
      videoBefore.removeEventListener('seeked', handleSeeked);
    };
  }, [beforeSrc, afterSrc]);

  return (
    <div
      ref={containerRef}
      className="relative w-full aspect-video overflow-hidden cursor-ew-resize select-none group rounded-lg border border-white/[0.08]"
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      onMouseMove={handleMouseMove}
      onTouchMove={handleTouchMove}
      onClick={handleClick}
    >
      {/* AFTER video — base layer */}
      <video
        ref={videoAfterRef}
        className="absolute inset-0 w-full h-full object-cover"
        src={afterSrc}
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
      />

      {/* BEFORE video — clipped overlay (no grayscale, natural colors) */}
      <div
        className="absolute inset-0 z-[2]"
        style={{ clipPath: `polygon(0 0, ${sliderPosition}% 0, ${sliderPosition}% 100%, 0 100%)` }}
      >
        <video
          ref={videoBeforeRef}
          className="absolute inset-0 w-full h-full object-cover"
          src={beforeSrc}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
        />
      </div>

      {/* Before label */}
      <div className="absolute top-3 left-3 sm:top-4 sm:left-4 z-[5] bg-black/70 backdrop-blur-md text-white/90 px-3 py-1.5 text-[10px] sm:text-[11px] font-mono tracking-[0.08em] uppercase border border-white/15 rounded-full pointer-events-none">
        {beforeLabel}
      </div>

      {/* After label */}
      <div className="absolute top-3 right-3 sm:top-4 sm:right-4 z-[5] bg-[#E50914] text-white px-3 py-1.5 shadow-[0_0_16px_rgba(229,9,20,0.4)] text-[10px] sm:text-[11px] font-mono font-medium tracking-[0.08em] uppercase rounded-full pointer-events-none">
        {afterLabel}
      </div>

      {/* Slider handle */}
      <div
        className="absolute top-0 bottom-0 z-10 pointer-events-none"
        style={{ left: `${sliderPosition}%`, transform: 'translateX(-50%)' }}
      >
        {/* Vertical line */}
        <div className="absolute top-0 bottom-0 left-1/2 -translate-x-1/2 w-0.5 bg-white" style={{ boxShadow: '0 0 12px rgba(255,255,255,0.3)' }} />

        {/* Circle handle */}
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 bg-[#E50914] text-white rounded-full flex items-center justify-center"
          style={{ boxShadow: '0 0 20px rgba(255,255,255,0.4)' }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round">
            <path d="M18 8L22 12L18 16" />
            <path d="M6 8L2 12L6 16" />
          </svg>
        </div>
      </div>

      {/* Play/Pause button */}
      <button
        className="absolute bottom-4 right-4 z-[15] opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white/90 text-black p-2.5 rounded-full hover:bg-white backdrop-blur-sm"
        onClick={togglePlayPause}
      >
        {isPlaying ? (
          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
            <rect x="6" y="4" width="4" height="16" />
            <rect x="14" y="4" width="4" height="16" />
          </svg>
        ) : (
          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
            <polygon points="5,3 19,12 5,21" />
          </svg>
        )}
      </button>
    </div>
  );
};

export default BeforeAfterComparison;
